import { ChangeDetectionStrategy, Component, Input, signal } from '@angular/core';
import { ArticlesService } from '../../../pages/articles/core/ArticlesService';
import { finalize } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { environment } from '../../../../environments/environment';
import * as moment from 'moment';
import { ISimpleListItem } from '../../../core/interfaces/ISimpleListItem';
import { IArticleVariant } from '../../../core/interfaces';
import { IArticleSalesHistoryItem } from '../../../pages/articles/core/interfaces/IArticleSalesHistoryItem';
import { IArticlePriceSheet } from '../../../pages/articles/core/interfaces/IArticlePriceSheet';
import { LocalStorageService, NotificationService } from '../../../core/services';

type Modes = 'yesterday' | 'thisWeek' | 'thisMonth' | 'thisYear' | 'custom';

@Component({
    selector: 'app-article-sales-history-table',
    templateUrl: './article-sales-history-table.component.html',
    styleUrl: './article-sales-history-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ArticleSalesHistoryTableComponent {

  @Input() set articleId(val: number) {
    this._articleId = val;

    const savedFilters = this._localStorageService.getItem(
      'articleSalesHistoryTable'
    ) as { selectedMode: Modes, selectedRange: Date[] };

    if (savedFilters) {
      this.selectedMode = savedFilters.selectedMode;
      this.selectedRange = savedFilters.selectedRange;
    } else {
      this.selectedMode = 'thisYear';
    }

    this.loadData();
  }

  @Input() stores: ISimpleListItem[] = [];
  @Input() articleVariants: IArticleVariant[] = [];
  @Input() isNonPackable = false;

  public selectedMode: Modes = 'thisYear';
  public articleSalesHistory: IArticleSalesHistoryItem[] = [];
  public priceSheets: Record<number, IArticlePriceSheet> = {};
  public totalAmount: number;
  public totalCount: number;
  public selectedRange: Date[] = [];
  public readonly dateFormat = environment.defaultDateFormat;
  public readonly isLoading = signal(false);

  private _articleId: number;

  public constructor(
    private readonly _articlesService: ArticlesService,
    private readonly _notifyService: NotificationService,
    private readonly _localStorageService: LocalStorageService,
  ) {}

  public sheetFor(variantId: number): IArticlePriceSheet | undefined {
    return this.priceSheets[variantId];
  }

  public vatTooltip(sheet?: IArticlePriceSheet): string {
    if (sheet?.taxRate == null) {
      return 'MwSt. nicht gepflegt';
    }

    return `MwSt. ${sheet.taxRate} %`;
  }

  public loadData(): void {
    this.isLoading.set(true);

    let dateFrom: moment.Moment;
    let dateTo: moment.Moment;

    if (this.selectedMode === 'custom') {
      dateFrom = moment(this.selectedRange[0]);
      dateTo = moment(this.selectedRange[1]);
    } else {
      this.selectedRange = [];

      if (this.selectedMode === 'thisYear') {
        dateFrom = moment().startOf('year');
        dateTo = moment().endOf('year');
      } else if (this.selectedMode === 'thisMonth') {
        dateFrom = moment().startOf('month');
        dateTo = moment().endOf('month');
      } else if (this.selectedMode === 'yesterday') {
        dateFrom = moment().subtract(1, 'day').startOf('day');
        dateTo = moment().subtract(1, 'day').endOf('day');
      } else if (this.selectedMode === 'thisWeek') {
        dateFrom = moment().startOf('week');
        dateTo = moment().endOf('week');
      }
    }

    this._localStorageService.setItem(
      'articleSalesHistoryTable',
      { selectedMode: this.selectedMode, selectedRange: this.selectedRange }
    );

    forkJoin({
      sales: this._articlesService.getArticleSalesHistory(this._articleId, dateFrom, dateTo),
      prices: this._articlesService.getArticlePriceSheet(this._articleId, dateFrom, dateTo),
    })
      .pipe(
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: ({ sales, prices }) => {
          this.articleSalesHistory = sales;
          this.priceSheets = (prices ?? []).reduce((acc, row) => {
            acc[row.articleVariantId] = row;
            return acc;
          }, {} as Record<number, IArticlePriceSheet>);
          this.totalAmount = this.articleSalesHistory.reduce((acc, item) => acc + item.amount, 0);
          this.totalCount = this.articleSalesHistory.reduce((acc, item) => acc + item.count, 0);
        },
        error: () => this._notifyService.pushError('Fehler', `Fehler beim Laden der Verkaufshistorie.`)
      });
  }
}
